import { registerService } from './lib';

export type MessagingProvider = 'monty' | 'twilio';

export type MessagingConfig = {
  whatsapp_provider: MessagingProvider;
  sms_provider: MessagingProvider;
};

export type AccountLoginActivity = {
  country: string;
  created_at: string;
  device: string;
  ip: string;
  key: string;
  login_method: string;
  through?: string | null;
};

export type AccountKycStatus = 'Verified' | 'Verification Failed' | 'Approving';

export type AccountKycInfo = {
  first_name: string;
  last_name: string;
  date_of_birth: string;
  address: string;
};

export type AccountKycDocs = {
  id_card_front_image: string;
  id_card_back_image?: string;
  selfie_file: string;
};

export type AccountUser = {
  key: string;
  email: null | string;
  phone_number: null | string;
  full_name: null | string;
  kyc_info_status: null | AccountKycStatus;
  kyc_docs_status: null | AccountKycStatus;
  last_login: null | string;
  totp_verified: boolean;
  username: string;
  features: string[];
  external_id: string;
  referral_code: string;
};

export type AccountUserDetail = {
  kyc_info_set: (AccountKycInfo & {
    key: string;
    status: string;
    errors: AccountKycInfo;
  })[];
  kyc_docs_set: (AccountKycDocs & {
    key: string;
    nonce: string;
    status: AccountKycStatus;
    errors: AccountKycDocs;
  })[];
  key: string;
  email: null | string;
  phone_number: null | string;
  full_name: null | string;
  kyc_info_status: null | AccountKycStatus;
  kyc_docs_status: null | AccountKycStatus;
  last_login: null | string;
  totp_verified: boolean;
  username: string;
  features: string[];
  external_id: string;
  referral_code: string;
};

export type AccountAuthenticationMethod =
  | 'PHONE_NUMBER'
  | 'EMAIL'
  | 'GOOGLE_AUTHENTICATOR';

export type AccountPhoneChannel = 'sms' | 'whatsapp';

export type AccountEmailPhoneLoginParams =
  | {
      email: string;
    }
  | {
      phone_number: string;
      channel: AccountPhoneChannel;
    };

export type AccountVerifyOtpParams = AccountEmailPhoneLoginParams & {
  nonce: string;
};

export type AccountGoogleLoginParams = {
  id_token: string;
};

export type AccountLoginSuccess = { message: 'ok' | unknown; created: boolean };

export type AccountAccessToken = { access_token: string };

export type AccountRequestMfaCodeParams = {
  slug: string;
  authentication_method: AccountAuthenticationMethod;
  phone_channel: AccountPhoneChannel;
};

export type AccountVerifyMfaCodeParams = {
  slug: string;
  authentication_method: AccountAuthenticationMethod;
  verification_code: string;
};

export type AccountRequestBindEmailParams = {
  email: string;
};

export type AccountVerifyEmailParams = AccountRequestBindEmailParams & {
  code: string;
};

export type AccountRequestBindPhoneNumberParams = {
  phone_number: string;
  phone_channel: AccountPhoneChannel;
};

export type AccountVerifyPhoneNumberParams = {
  phone_number: string;
  code: string;
};

declare module './lib' {
  interface ServiceRegistry {
    account: ServiceEndpoints<{
      'post:auth/email-or-phone-login/': {
        params: AccountEmailPhoneLoginParams; // & { captcha_token: string; captcha_v2_token?: string }
      };
      'post:auth/verify-otp/': {
        params: AccountVerifyOtpParams; // & { referrer_code?: string; through?: string;};
        response: AccountLoginSuccess;
      };
      'post:auth/logout/': {};
      'post:auth/google-login/': {
        params: AccountGoogleLoginParams; // & { referrer_code?: string; through?: string;};
        response: AccountLoginSuccess;
      };
      'post:auth/access-token/': {
        response: AccountAccessToken;
      };
      'get:auth/login-activities/': {
        response: AccountLoginActivity[];
      };
      'post:auth/setup/email': {
        params: AccountRequestBindEmailParams;
      };
      'post:auth/setup/verify-email': {
        params: AccountVerifyEmailParams;
      };
      'post:auth/setup/phone-number': {
        params: AccountRequestBindPhoneNumberParams;
      };
      'post:auth/setup/verify-phone-number': {
        params: AccountVerifyPhoneNumberParams;
      };
      'post:auth/setup/totp': {
        response: {
          secret: string;
        };
      };
      'post:auth/setup/verify-totp': {
        params: {
          code: string;
        };
      };
      'post:mfa/{slug}/request-code': {
        params: AccountRequestMfaCodeParams;
        response: any;
      };
      'post:mfa/{slug}/verify-code': {
        params: AccountVerifyMfaCodeParams;
      };
      'post:mfa/{slug}/reject': {
        params: {
          slug: string;
        };
      };
      'get:kyc/info': {
        response: AccountKycInfo & {
          errors?: Partial<AccountKycInfo> | null;
          key: string;
          status: string;
        };
      };
      'post:kyc/submit-info': {
        params: AccountKycInfo;
      };
      'get:kyc/docs': {
        response: AccountKycDocs & {
          errors?: Partial<AccountKycDocs> | null;
          key: string;
          nonce: string;
          status: string;
        };
      };
      'post:kyc/submit-docs': {
        params: AccountKycDocs & {
          token: string;
        };
      };
      'get:kyc/token': {
        response: { token: string };
      };
      'get:users/me': {
        response: AccountUser;
      };
      'get:user-storage/{slug}': {
        params: {
          slug: string;
        };
        response: {
          value: string | boolean | null;
        };
      };
      'post:user-storage/{slug}': {
        params: {
          slug: string;
          value: string | boolean;
        };
      };
      'delete:user-storage/{slug}': {
        params: {
          slug: string;
        };
      };
    }>;
    'account-panel': ServiceEndpoints<{
      'get:users/{slug}/': {
        params: {
          slug: string;
        };
        response: AccountUserDetail;
      };
      'get:users/': {
        params: {
          search?: string;
          kyc_docs_status?: string;
          kyc_info_status?: string;
        };
        response: AccountUser[];
      };
      'patch:kyc-docs/{slug}/': {
        params: {
          slug: string;
          status: AccountKycStatus;
          errors: Partial<AccountKycDocs>;
        };
        response: {
          status: AccountKycStatus;
          errors: Partial<AccountKycDocs>;
        };
      };
      'patch:kyc-info/{slug}/': {
        params: {
          slug: string;
          status: AccountKycStatus;
          errors: Partial<AccountKycInfo>;
        };
        response: {
          status: AccountKycStatus;
          errors: Partial<AccountKycInfo>;
        };
      };
      'get:nonces/': {
        response: Record<string, string>;
      };
      'get:messaging-config': {
        response: MessagingConfig;
      };
      'put:messaging-config': {
        params: MessagingConfig;
        response: MessagingConfig;
      };
    }>;
  }
}

registerService('account', '/api/v1/account');
registerService('account-panel', '/api/panel');
